/**
 * PageRenderer Component
 * Fetches and renders tenant pages created with Puck editor
 * Falls back to background image if no main page exists
 */

import React, { useState, useEffect } from 'react';
import { Render } from '@measured/puck';
import { resolveTenant } from '@whitelabel/utils';
import './PageRenderer.css';

// Import Puck components (you'll need to match these with your admin puck config)
import { puckConfig } from './puckConfig';

const PageRenderer = ({ slug = null }) => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPlayingUrl, setCurrentPlayingUrl] = useState(null);
  const tenant = resolveTenant();

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);

        // Fetch either by slug or main page
        const endpoint = slug 
          ? `/api/contentmanagement/pages/by-slug/${slug}`
          : `/api/contentmanagement/pages/main`;
        
        const response = await fetch(endpoint, {
          headers: {
            'X-Tenant-ID': tenant,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('[PageRenderer] Page data:', data, 'slug:', slug);
          console.log('[PageRenderer] Page content:', data.page?.content);
          console.log('[PageRenderer] Active version ID:', data.page?.active_version_id);
          setPageData(data.page);
          setError(null);
        } else if (response.status === 404) {
          console.log('[PageRenderer] Page not found, using background image');
          setPageData(null);
          setError(slug ? `Page "${slug}" not found` : null);
        } else {
          throw new Error(`Failed to fetch page: ${response.statusText}`);
        }
      } catch (err) {
        console.error('[PageRenderer] Error fetching page:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [tenant, slug]);

  // Listen for playAudio events to track currently playing
  useEffect(() => {
    const handlePlayAudio = (e) => {
      setCurrentPlayingUrl(e.detail.url);
    };
    
    const handleBackToLive = () => {
      console.log('[PageRenderer] Back to live - clearing active card');
      setCurrentPlayingUrl(null);
    };
    
    window.addEventListener('playAudio', handlePlayAudio);
    window.addEventListener('backToLive', handleBackToLive);
    
    return () => {
      window.removeEventListener('playAudio', handlePlayAudio);
      window.removeEventListener('backToLive', handleBackToLive);
    };
  }, []);

  if (loading) {
    return <div className="page-renderer-loading">Loading page...</div>;
  }

  if (error && slug) {
    // Show error for slug-based pages
    const handleHomeClick = (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('navigateTo', { detail: { url: '/' } }));
    };

    return (
      <div className="page-renderer-error">
        <h2>Page Not Found</h2>
        <p>{error}</p>
        <a href="/" onClick={handleHomeClick} style={{ color: '#3b82f6', textDecoration: 'underline' }}>
          Return to Home
        </a>
      </div>
    );
  }

  // Support both content formats (active_version_content from backend, or content)
  const pageContent = pageData?.active_version_content || pageData?.content;

  if (error || !pageData || !pageContent) {
    console.log('[PageRenderer] No page data or content, using background image');
    return null; // Fall back to background image
  }

  // Pass currentPlayingUrl to puckConfig via a global
  window.__CURRENT_PLAYING_URL__ = currentPlayingUrl;

  return (
    <div className="page-renderer">
      <Render config={puckConfig} data={pageContent} />
    </div>
  );
};

export default PageRenderer;
